import Divider from "@mui/material/Divider"
import Stack from "@mui/material/Stack"
import { FC } from "react"

import { useAccount } from "../../../../states/account"
import { useBooking } from "../../../../states/bookingDetail"
import { useBookings } from "../../../../states/bookings"
import { Accordion } from "src/components/common/accordion"
import { create } from "src/helpers/bem"

import { formatPrice } from "src/helpers/price"
import { useLocale } from "src/states/locale"

import {
  xRMWebshopOrderedCategories,
  xRMWebshopOrderedItem,
} from "src/types/xRM"

import { OrderRow } from "./OrderRow"
import styles from "./Orders.module.scss"

const bem = create(styles, "Orders")

export interface OrdersProps {
  activeDebitorId: string
  data: xRMWebshopOrderedCategories[]
}

export const Orders: FC<OrdersProps> = ({ activeDebitorId, data }) => {
  const locale = useLocale()
  const { booking } = useBooking()
  const { bookings } = useBookings()
  const {
    contact: { debitorId, id: contactId } = {},
    firstName,
    lastName,
  } = useAccount()

  const activeBooking = bookings.find(
    (actBooking) => actBooking.id === booking?.id,
  )
  const currentUserName =
    firstName && lastName ? `${firstName} ${lastName}` : ""
  const isUserOrdererOfCategory = (category: xRMWebshopOrderedCategories) => {
    return (category.items ?? []).some(
      (item) => item.senderName === currentUserName,
    )
  }
  const showPrices =
    debitorId === activeDebitorId ||
    contactId === activeBooking?.senderContactId ||
    contactId === activeBooking?.contactId
  const showCategoryPrices = (category: xRMWebshopOrderedCategories) => {
    return showPrices || isUserOrdererOfCategory(category)
  }

  const renderCategories = (category: xRMWebshopOrderedCategories) => {
    return (
      <Accordion
        key={category.categoryId}
        disableGutters
        colorVariant={"base"}
        detailsClassName={bem("accordionDetails")}
        id={category.categoryId!}
        label={category.categoryName}
        description={
          showCategoryPrices(category)
            ? formatPrice(category.totalAmount, locale)
            : undefined
        }
        sx={{
          paddingLeft: 0,
        }}
      >
        <Stack
          className={bem("stack")}
          divider={<Divider className={bem("divider")} variant="fullWidth" />}
        >
          {renderProducts(category.items ?? [])}
        </Stack>
      </Accordion>
    )
  }

  const renderProducts = (items: xRMWebshopOrderedItem[]) => {
    return items.map((item, index) => (
      <OrderRow
        key={item.id + index.toString()}
        activeDebitorId={activeDebitorId}
        item={item}
        ordererName={item.senderName ?? ""}
      />
    ))
  }

  return (
    <div className={bem()}>
      <Stack spacing={2}>
        {data.length !== 0 && data.map((item) => renderCategories(item))}
      </Stack>
    </div>
  )
}
